let connectionCleanup;
let latestState;
const subscribers = new Set();

// One connection per iframe, shared by IMA's one-shot wait and GTM's persistent consent subscription.
export const subscribeToParentCmp = (listener) => {
	if (window.self === window.top) return () => {};
	subscribers.add(listener);
	if (latestState) listener(latestState);
	if (!connectionCleanup) {
		connectionCleanup = connectToParentCmp((state) => {
			latestState = state;
			for (const subscriber of [...subscribers]) subscriber(state);
		});
	}
	return () => {
		subscribers.delete(listener);
		if (subscribers.size > 0) return;
		connectionCleanup?.();
		connectionCleanup = undefined;
		latestState = undefined;
	};
};

// Find the ancestor CMP without reading its cross-origin document or selecting a nested embed CMP.
const findParentCmpFrame = () => {
	let candidate = window.parent;
	while (candidate) {
		try {
			if (candidate.frames.__tcfapiLocator) return candidate;
		} catch {
			// Cross-origin ancestors may reject property access while allowing postMessage.
		}
		if (candidate === window.top) break;
		candidate = candidate.parent;
	}
	return window.top;
};

// Bind TCF and Consentmanager status replies to the selected ancestor and their matching call IDs.
const connectToParentCmp = (emit) => {
	let parentFrame;
	let tcData;
	let statusCallId;
	let receivedResponse = false;
	const listenerIds = new Set();
	const createCallId = () => `asport-cmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const subscriptionCallId = createCallId();
	const send = (message) => {
		try {
			parentFrame.postMessage(message, '*');
		} catch {
			// Parent navigation can temporarily make the CMP unavailable; retries never grant consent.
		}
	};
	const handleMessage = (messageEvent) => {
		if (messageEvent.source !== parentFrame) return;
		let response = messageEvent.data;
		if (typeof response === 'string') {
			try {
				response = JSON.parse(response);
			} catch {
				return;
			}
		}
		const tcfReply = response?.__tcfapiReturn;
		if (tcfReply?.callId === subscriptionCallId) {
			receivedResponse = !!(tcfReply.success && tcfReply.returnValue);
			tcData = receivedResponse ? tcfReply.returnValue : undefined;
			statusCallId = createCallId();
			if (tcData?.listenerId) listenerIds.add(tcData.listenerId);
			// Readiness alone is sufficient for IMA. GTM must also verify an explicit choice.
			emit({ tcData, success: receivedResponse, hasExplicitChoice: tcData?.eventStatus === 'useractioncomplete' ? true : undefined });
			if (receivedResponse) send({ __cmpCall: { command: 'consentStatus', parameter: null, callId: statusCallId } });
		}
		const statusReply = response?.__cmpReturn;
		if (tcData && statusReply?.callId === statusCallId && statusReply.success) {
			emit({ tcData, success: true, hasExplicitChoice: statusReply.returnValue?.userChoiceExists === true });
		}
	};
	const subscribe = () => {
		if (receivedResponse) return;
		// The locator can disappear after startup while the top frame still handles CMP messages.
		parentFrame = findParentCmpFrame();
		send({ __tcfapiCall: { command: 'addEventListener', version: 2, parameter: null, callId: subscriptionCallId } });
	};
	window.addEventListener('message', handleMessage);
	subscribe();
	const interval = window.setInterval(subscribe, 200);
	return () => {
		window.clearInterval(interval);
		window.removeEventListener('message', handleMessage);
		for (const listenerId of listenerIds) {
			send({ __tcfapiCall: { command: 'removeEventListener', version: 2, parameter: listenerId, callId: createCallId() } });
		}
	};
};
